import React from "react";
import { WarningOctagon, X, SpinnerGap } from "@phosphor-icons/react";

// Modal asking the user to retype their email before destroying the
// account. Owns its own input + error state; the caller supplies the
// actual delete callback (so the redirect/state-clear logic lives next
// to the AuthContext that handles the API call).
export default function DeleteAccountModal({ open, onClose, onConfirm, userEmail, t }) {
    const [confirmEmail, setConfirmEmail] = React.useState("");
    const [deleting, setDeleting] = React.useState(false);
    const [deleteError, setDeleteError] = React.useState("");

    React.useEffect(() => {
        if (!open) {
            setConfirmEmail("");
            setDeleteError("");
            setDeleting(false);
        }
    }, [open]);

    if (!open) return null;

    const closeIfIdle = () => { if (!deleting) onClose(); };

    const onSubmit = async (e) => {
        e?.preventDefault();
        if (!userEmail) return;
        if (confirmEmail.trim().toLowerCase() !== userEmail.toLowerCase()) {
            setDeleteError(t("settings.danger.email_mismatch"));
            return;
        }
        setDeleting(true);
        setDeleteError("");
        const ok = await onConfirm(confirmEmail.trim().toLowerCase());
        if (!ok) {
            setDeleting(false);
            setDeleteError(t("settings.danger.error"));
        }
        // On success the parent will navigate away — no need to reset.
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-stone900/60 backdrop-blur-sm"
            onClick={closeIfIdle}
            data-testid="delete-account-modal-backdrop"
        >
            <form
                className="w-full max-w-md bg-white rounded-lg shadow-xl border border-red-200 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                onSubmit={onSubmit}
                data-testid="delete-account-modal"
            >
                <header className="flex items-start gap-3 p-5 border-b border-sand-300">
                    <span
                        className="shrink-0 w-9 h-9 rounded-full bg-red-100 text-red-700 flex items-center justify-center mt-0.5"
                        aria-hidden="true"
                    >
                        <WarningOctagon size={18} weight="duotone" />
                    </span>
                    <div className="flex-1 min-w-0">
                        <p className="label-eyebrow text-red-700 mb-1">
                            {t("settings.danger.modal.eyebrow")}
                        </p>
                        <h3 className="heading-serif text-xl tracking-tight m-0">
                            {t("settings.danger.modal.title")}
                        </h3>
                    </div>
                    <button
                        type="button"
                        onClick={closeIfIdle}
                        disabled={deleting}
                        aria-label="Close"
                        data-testid="delete-account-modal-close"
                        className="text-stoneFaint hover:text-stone900 disabled:opacity-50"
                    >
                        <X size={18} weight="bold" />
                    </button>
                </header>

                <div className="p-5">
                    <p className="ui-sans text-sm leading-relaxed text-stone900 mb-4">
                        {t("settings.danger.modal.body")}
                    </p>
                    <label className="block ui-sans text-xs font-semibold uppercase tracking-wider text-stoneMuted mb-2">
                        {t("settings.danger.modal.email_label", { email: userEmail })}
                    </label>
                    <input
                        type="email"
                        inputMode="email"
                        autoComplete="off"
                        spellCheck={false}
                        value={confirmEmail}
                        onChange={(e) => setConfirmEmail(e.target.value)}
                        placeholder={userEmail}
                        data-testid="delete-account-email-input"
                        className="w-full px-3 py-2.5 border border-sand-300 rounded-md focus:outline-none focus:border-red-700 ui-sans text-sm"
                        disabled={deleting}
                        autoFocus
                    />
                    {deleteError && (
                        <p
                            className="mt-3 ui-sans text-sm text-red-700"
                            data-testid="delete-account-error"
                            role="alert"
                        >
                            {deleteError}
                        </p>
                    )}
                </div>

                <footer className="flex items-center justify-end gap-3 p-5 bg-sand-100 border-t border-sand-300">
                    <button
                        type="button"
                        onClick={closeIfIdle}
                        disabled={deleting}
                        data-testid="delete-account-cancel-btn"
                        className="px-4 py-2 ui-sans font-semibold text-stoneMuted hover:text-stone900 disabled:opacity-50"
                    >
                        {t("settings.danger.modal.cancel")}
                    </button>
                    <button
                        type="submit"
                        disabled={deleting || !confirmEmail.trim()}
                        data-testid="delete-account-confirm-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-700 hover:bg-red-800 text-white ui-sans font-semibold rounded-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {deleting && <SpinnerGap size={14} weight="bold" className="animate-spin" />}
                        {deleting
                            ? t("settings.danger.modal.deleting")
                            : t("settings.danger.modal.confirm")}
                    </button>
                </footer>
            </form>
        </div>
    );
}
